import {
    ContentTypeItem,
    FolderItem,
    FolderMapInterface,
    HierarchyFolder,
    HomePageFilterState,
    HomePageSettings,
    HomePageState,
    TagItem,
    TileInfo,
} from 'app/home/index.functions';
import { TFunction } from 'i18next';
import { foldersAPI } from 'api/folder';

export type FilterType = {
    field: string;
    operator: string;
    data: any;
};

export type TileSection = {
    title: string;
    titleData?: HierarchyFolder[]; // Used for folder hierarchy
    k: string;
    tiles: TileInfo[];
};

export const filterTiles = (
    tiles: TileInfo[],
    tags: TagItem[],
    folderMap: FolderMapInterface,
    settings: HomePageSettings,
    filterState: HomePageFilterState,
    isEmbedded?: boolean,
): TileInfo[] => {
    let filteredTiles: TileInfo[] = [];

    let filters: FilterType[] = [];
    let isKeywordFilter = false;

    if (filterState) {
        if (filterState.groupBy != '' && !filterState.folderSelected.startsWith('favorite_')) {
            filters.push({
                field: 'favorite_id',
                operator: 'eq',
                data: 0,
            });
        }

        if (filterState.folderSelected != '') {
            if (filterState.folderSelected.startsWith('favorite_')) {
                filters.push({
                    field: 'favorite_id',
                    operator: 'eq',
                    data: parseInt(filterState.folderSelected.replace('favorite_', '')),
                });
            }

            if (
                filterState.folderSelected.startsWith('category_') ||
                filterState.folderSelected.startsWith('folder_')
            ) {
                let prefix = filterState.folderSelected.startsWith('category_') ? 'category_' : 'folder_';
                let catId = parseInt(filterState.folderSelected.replace(prefix, ''));
                let catIds: number[] = [catId];

                const processFolders = (prefix: string, catId: number) => {
                    if (
                        undefined !== folderMap[prefix + catId] &&
                        undefined !== folderMap[prefix + catId].children &&
                        undefined !== folderMap[prefix + catId]?.children?.length &&
                        folderMap[prefix + catId]!.children!.length > 0
                    )
                        for (let i = 0; i < folderMap[prefix + catId]!.children!.length; i++) {
                            //@ts-ignore
                            if (undefined !== folderMap[prefix + catId]!.children![i]![prefix + 'id']) {
                                let subCatId = parseInt(
                                    //@ts-ignore
                                    folderMap[prefix + catId]!.children![i]![prefix + 'id'] || '0',
                                );
                                catIds.push(subCatId);
                                processFolders(prefix, subCatId);
                            }
                        }
                };

                processFolders(prefix, catId);

                filters.push({
                    field: prefix + 'id',
                    operator: 'folder_' === prefix ? 'intersect' : 'in',
                    data: catIds,
                });
            }
        }

        if (filterState.segmentFilter.length > 0) {
            if (filterState.segmentFilter.match(/new_alerts_\w*/)) {
                filters.push({ field: 'is_alert_active', operator: 'eq', data: 1 });
                filters.push({
                    field: 'last_alert_news_type',
                    operator: 'eq',
                    data: filterState.segmentFilter.replace('new_alerts_', ''),
                });
            } else if ('owned' == filterState.segmentFilter) {
                filters.push({
                    field: 'is_owned',
                    operator: 'eq',
                    data: 1,
                });
            } else if (filterState.segmentFilter.match(/stoplight_\d*/)) {
                filters.push({
                    field: 'last_stoplight_value',
                    operator: 'eq',
                    data: filterState.segmentFilter.replace('stoplight_', ''),
                });
            } else if (filterState.segmentFilter.match(/certified_\w*/)) {
                filters.push({
                    field: 'certified_ind',
                    operator: 'eq',
                    data: filterState.segmentFilter.replace('certified_', ''),
                });
            } else if (filterState.segmentFilter.match(/certification_level_\d*/)) {
                filters.push({
                    field: 'certified_ind',
                    operator: 'eq',
                    data: 'Y',
                });
                filters.push({
                    field: 'certification_level_id',
                    operator: 'eq',
                    data: filterState.segmentFilter.replace('certification_level_', ''),
                });
            } else if (filterState.segmentFilter.indexOf('content_') === 0) {
                filters.push({
                    field: 'content_type',
                    operator: 'eq',
                    data: filterState.segmentFilter.replace('content_', ''),
                });
            } else {
                filters.push({
                    field: 'segment_value_id',
                    operator: 'eq',
                    data: filterState.segmentFilter,
                });
            }
        }

        if (filterState.tagFilter.length > 0)
            filters.push({ field: 'topics', operator: 'intersect', data: filterState.tagFilter });

        isKeywordFilter = filterState.keywordFilter.length > 0;

        if (isKeywordFilter) {
            filters.push({
                field: 'element_dashboard_name',
                operator: 'likeExtended',
                data: filterState.keywordFilter,
            });
        } else {
            if (
                !isEmbedded &&
                'owned' != filterState.segmentFilter &&
                'N' == settings?.parameters?.show_categories_on_homepage_ind &&
                !(filterState.folderSelected != '' && filterState.folderSelected.startsWith('folder_'))
            )
                filters.push({
                    field: 'category_id',
                    operator: 'inFolders',
                    data: true,
                });
        }
    }

    let topicsForSearchText: { [key: string]: string } | null = null;

    filteredTiles = tiles.filter((tile) => {
        let found = true;

        if (
            'N' == tile.has_access &&
            !isKeywordFilter &&
            'Y' !== settings?.parameters?.show_categories_on_homepage_ind &&
            'Y' !== settings?.parameters?.SHOW_DISCOVERABLE_CONTENT_IN_FOLDERS
        )
            return false;

        for (const filter of filters) {
            if (filter.operator === 'eq') {
                // @ts-ignore
                if (!(tile[filter.field] == filter.data)) {
                    found = false;
                    break;
                }
            } else if (filter.operator === 'in') {
                // @ts-ignore
                if (!filter.data.includes(tile[filter.field])) {
                    found = false;
                    break;
                }
            } else if (filter.operator === 'intersect') {
                // @ts-ignore
                if (0 === filter.data.filter((x) => tile[filter.field].includes(x)).length) {
                    found = false;
                    break;
                }
            } else if (filter.operator === 'inFolders') {
                if (!(tile.favorite_id != '0' || (undefined !== tile.folder_id && tile.folder_id.length > 0))) {
                    found = false;
                    break;
                }
            } else if (filter.operator === 'likeExtended') {
                if (null === topicsForSearchText) {
                    topicsForSearchText = {};
                    for (let j = 0; j < tags.length; j++)
                        topicsForSearchText[tags[j].topic_id] = tags[j].full_name.toLowerCase();
                }

                let flag = false,
                    condTxt = filter.data.toLowerCase(),
                    val =
                        tile.element_id +
                        '#*!' +
                        (tile.element_dashboard_name || '') +
                        (tile.element_info ? '#*!' + tile.element_info.replace(/&shy;/g, '') : '');

                let condCompare = condTxt;
                if (-1 !== condTxt.indexOf('"')) condCompare = condTxt.replace(/"/g, '');
                if (tile.element_dashboard_name && tile.element_dashboard_name.toLowerCase() == condCompare) {
                    flag = true;
                    tile.search_relevance = 1;
                } else if (val.toLowerCase().indexOf(condCompare) !== -1) {
                    flag = true;
                    tile.search_relevance = 2;
                } else {
                    let lemmas: string[] = [],
                        tmpVal = val.toLowerCase();

                    let currentLemma = '';
                    if (-1 === condTxt.indexOf('"')) {
                        lemmas = condTxt.split(' ');
                    } else {
                        let inQuote = false;
                        for (let i = 0; i < condTxt.length; i++) {
                            if (condTxt[i] === ' ') {
                                if (inQuote) {
                                    currentLemma += condTxt[i];
                                } else {
                                    if (currentLemma.length > 0) lemmas.push(currentLemma);
                                    currentLemma = '';
                                }
                            } else if (condTxt[i] === '"' && (i === 0 || condTxt[i - 1] !== '\\')) {
                                inQuote = !inQuote;

                                if (inQuote) {
                                    if (currentLemma.length > 0) lemmas.push(currentLemma);
                                    currentLemma = '';
                                } else {
                                    lemmas.push(currentLemma);
                                    currentLemma = '';
                                }
                            } else if (
                                !(inQuote && condTxt[i] === '\\' && (i < condTxt.length - 1 || condTxt[i + 1] === '"'))
                            ) {
                                currentLemma += condTxt[i];
                            }
                        }
                        if (currentLemma.length > 0) lemmas.push(currentLemma);
                    }

                    if (null !== topicsForSearchText)
                        if (tile.topics && tile.topics.length)
                            for (let j = 0; j < tile.topics.length; j++)
                                if (topicsForSearchText[tile.topics[j]])
                                    tmpVal += ' ' + topicsForSearchText[tile.topics[j]];

                    tmpVal +=
                        (tile.business_owner ? '#*!' + (tile.business_owner ?? '').toLowerCase() : '') +
                        (tile.data_steward ? '#*!' + (tile.data_steward ?? '').toLowerCase() : '') +
                        (tile.technical_owner ? '#*!' + (tile.technical_owner ?? '').toLowerCase() : '');

                    tmpVal += tile.data_source_name ? '#*!' + (tile.data_source_name ?? '').toLowerCase() : '';

                    if (lemmas.length > 0) {
                        flag = true;
                        for (let key in lemmas)
                            if (lemmas.hasOwnProperty(key))
                                if (tmpVal.indexOf(lemmas[key]) === -1) {
                                    flag = false;
                                    break;
                                }
                    }

                    if (flag) tile.search_relevance = 3;
                }

                if (!flag) {
                    found = false;
                    break;
                }
            }
        }

        return found;
    });

    return filteredTiles;
};

export const filterHierarchy = (
    favorites: HierarchyFolder[],
    folders: HierarchyFolder[],
    categories: HierarchyFolder[],
    filterState: HomePageFilterState,
): HierarchyFolder[] => {
    let filteredHierarchy: HierarchyFolder[] = [];
    if (filterState.folderSelected) {
        if (filterState.folderSelected.startsWith('favorite_')) {
            for (const f of favorites) {
                if (f.k === filterState.folderSelected) {
                    filteredHierarchy.push(f);
                    break;
                }
            }
        } else if (filterState.folderSelected.startsWith('folder_')) {
            let filterFolders = function (c: HierarchyFolder[]) {
                for (const f of c)
                    if (f.k === filterState.folderSelected) {
                        filteredHierarchy.push(f);
                        break;
                    } else if (f.children) filterFolders(f.children);
            };

            filterFolders(folders);
        } else if (filterState.folderSelected.startsWith('category_')) {
            for (const f of categories) {
                if (
                    f.k === filterState.folderSelected ||
                    (undefined !== f.childrenKeys && f.childrenKeys.includes(filterState.folderSelected))
                ) {
                    filteredHierarchy.push(f);
                    break;
                }
            }
        }
    } else {
        for (const f of favorites) filteredHierarchy.push(f);
        for (const f of folders) filteredHierarchy.push(f);
        for (const f of categories) filteredHierarchy.push(f);
    }

    return filteredHierarchy;
};

export const DEFAULT_DISPLAY_ORDER = 1000000;
export const categorySort = (a: TileInfo, b: TileInfo) => {
    if (a.display_order === b.display_order) {
        return (
            a.element_dashboard_name?.toLowerCase().localeCompare(b?.element_dashboard_name?.toLowerCase() ?? '') ?? 0
        );
    } else {
        return a.display_order === b.display_order
            ? 0
            : (a.display_order ?? DEFAULT_DISPLAY_ORDER) > (b.display_order ?? DEFAULT_DISPLAY_ORDER)
              ? 1
              : -1;
    }
};

export const processSections = (
    tiles: TileInfo[],
    hierarchyFolders: HierarchyFolder[],
    favorites: HierarchyFolder[],
    folders: HierarchyFolder[],
    categories: HierarchyFolder[],
    tags: TagItem[],
    folderItems: FolderItem[],
    contentTypes: ContentTypeItem[],
    settings: HomePageSettings,
    folderMap: FolderMapInterface,
    homePageState: HomePageState,
    t: TFunction,
    isEmbedded?: boolean,
): TileSection[] => {
    let tilesSections: TileSection[] = [];

    let filteredTiles = filterTiles(tiles, tags, folderMap, settings, homePageState.filterState, isEmbedded);

    if (homePageState.filterState.groupBy === '') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles) {
            if (tile.category_id != '0') {
                let k = 'category_' + tile.category_id;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];

                if (
                    ('Category' !== tile.section_type || 'Y' === tile.in_dashboard_ind_flag) &&
                    (!(
                        'Category' == tile.section_type && 'N' == settings?.parameters?.show_categories_on_homepage_ind
                    ) ||
                        !('' == homePageState.filterState.keywordFilter) ||
                        'owned' == homePageState.filterState.segmentFilter)
                )
                    sectionsMap[k].push(tile);
            }

            if (tile.favorite_id != '0') {
                let k = 'favorite_' + tile.favorite_id;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }

            if (tile.favorite_id == '0' && tile.folder_id && tile.folder_id.length > 0)
                for (const j of tile.folder_id) {
                    let k = 'folder_' + j;
                    if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                    sectionsMap[k].push(tile);
                }
        }

        let filteredHierarchy = filterHierarchy(favorites, folders, categories, homePageState.filterState);

        const processFolderTitle = (f: HierarchyFolder): { title: string; data: Array<HierarchyFolder> } => {
            let parts: string[] = [f.name];
            const dataParts: Array<HierarchyFolder> = [f];
            let tmp: HierarchyFolder = f;

            while (tmp.parent_folder_id > 0) {
                let find = hierarchyFolders.find((x) => String(x.folder_id) === String(tmp.parent_folder_id));
                if (undefined !== find) {
                    tmp = find;
                    parts.push(tmp.name);
                    dataParts.push(tmp);
                } else break;
            }

            while (tmp.parent_category_id > 0) {
                let find = hierarchyFolders.find((x) => String(x.category_id) === String(tmp.parent_category_id));
                if (undefined !== find) {
                    tmp = find;
                    parts.push(tmp.name);
                    dataParts.push(tmp);
                } else break;
            }

            return { title: parts.reverse().join(' / '), data: dataParts.reverse() };
        };

        const processFolder = (f: HierarchyFolder) => {
            let k = f.k || '';
            if (undefined !== sectionsMap[k]) {
                const title = processFolderTitle(f);
                tilesSections.push({
                    title: title.title,
                    titleData: title.data,
                    k: k,
                    tiles: sectionsMap[k],
                });
            }

            if (f.children && f.children.length > 0) {
                for (const c of f.children) {
                    processFolder(c);
                }
            }
        };

        for (const f of filteredHierarchy) {
            let k = f.k || '';
            if (undefined !== sectionsMap[k]) {
                const title = processFolderTitle(f);

                tilesSections.push({
                    title: f.parent_folder_id > 0 || f.parent_category_id > 0 ? title.title : f.name,
                    titleData: title.data,
                    k: k,
                    tiles: sectionsMap[k],
                });
            }

            if (f.children && f.children.length > 0) for (const c of f.children) processFolder(c);
        }

        for (let tsk = 0; tsk < tilesSections.length; tsk++) {
            let hfk: HierarchyFolder | undefined;

            if (tilesSections[tsk].k.startsWith('folder_')) {
                let folderId = parseInt(tilesSections[tsk].k.replace('folder_', ''));
                hfk = hierarchyFolders.find((x) => x.folder_id === folderId);
                let folderItemsDisplayOrder: { [key: string]: number } = {};
                if (undefined !== hfk && 'Y' == hfk.sort_order_modified_by_owner_ind) {
                    for (const fi of folderItems)
                        if (fi.folder_id == hfk.folder_id)
                            folderItemsDisplayOrder[(fi.element_id ?? '') + '_' + (fi.segment_value_id ?? '')] =
                                fi.display_order ?? DEFAULT_DISPLAY_ORDER;

                    tilesSections[tsk].tiles.sort((a, b) => {
                        let a_display_order =
                            folderItemsDisplayOrder[(a.element_id ?? '') + '_' + (a.segment_value_id ?? '')] ??
                            DEFAULT_DISPLAY_ORDER;
                        let b_display_order =
                            folderItemsDisplayOrder[(b.element_id ?? '') + '_' + (b.segment_value_id ?? '')] ??
                            DEFAULT_DISPLAY_ORDER;

                        if (a_display_order === b_display_order) {
                            return (
                                a.element_dashboard_name
                                    ?.toLowerCase()
                                    .localeCompare(b?.element_dashboard_name?.toLowerCase() ?? '') ?? 0
                            );
                        } else {
                            return a_display_order === b_display_order ? 0 : a_display_order > b_display_order ? 1 : -1;
                        }
                    });
                }
            } else {
                if (tilesSections[tsk].k.startsWith('favorite_'))
                    hfk = favorites.find((x) => x.k === tilesSections[tsk].k);
                else if (tilesSections[tsk].k.startsWith('category_'))
                    hfk = categories.find((x) => x.k === tilesSections[tsk].k);

                if (undefined !== hfk && 'Y' == hfk.sort_order_modified_by_owner_ind)
                    tilesSections[tsk].tiles.sort(categorySort);
            }
        }
    } else if (homePageState.filterState.groupBy === 'Tag') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles) {
            if (tile.topics && tile.topics.length) {
                for (const tag of tile.topics) {
                    let k = 'tag_' + tag;
                    if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                    sectionsMap[k].push(tile);
                }
            } else {
                let k = 'tag_0';
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }
        }

        for (const tag of tags) {
            let k = 'tag_' + tag.topic_id;
            if (undefined !== sectionsMap[k])
                tilesSections.push({
                    title: tag.full_name,
                    k: k,
                    tiles: sectionsMap[k],
                });
        }

        let k = 'tag_0';
        if (undefined !== sectionsMap[k])
            tilesSections.push({
                title: t('none'),
                k: k,
                tiles: sectionsMap[k],
            });
    } else if (homePageState.filterState.groupBy === 'Content') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles) {
            if (tile.content_type) {
                let k = 'content_' + tile.content_type;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }
        }

        let kOther = 'content_' + t('other');
        for (const cti of contentTypes) {
            if (cti.content_type === kOther) continue;
            let k = 'content_' + cti.content_type;
            if (undefined !== sectionsMap[k])
                tilesSections.push({
                    title: cti.content_type,
                    k: k,
                    tiles: sectionsMap[k],
                });
        }

        if (undefined !== sectionsMap[kOther])
            tilesSections.push({
                title: t('other'),
                k: kOther,
                tiles: sectionsMap[kOther],
            });
    } else if (homePageState.filterState.groupBy === 'Stoplight') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles)
            if (undefined !== tile.last_stoplight_value) {
                let k = '' + tile.last_stoplight_value;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }

        let titles = [
            t('label_no_stoplight_value_set'),
            t('label_outperforming'),
            t('label_performing'),
            t('label_underperforming'),
        ];

        for (let i = 0; i < titles.length; i++) {
            let k = '' + i;
            if (undefined !== sectionsMap[k]) tilesSections.push({ title: titles[i], k: k, tiles: sectionsMap[k] });
        }
    } else if (homePageState.filterState.groupBy === 'MI') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles)
            if (undefined !== tile.mi_name) {
                let k = '' + tile.mi_name;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }

        for (let key in sectionsMap)
            if (undefined !== sectionsMap[key]) tilesSections.push({ title: key, k: key, tiles: sectionsMap[key] });

        tilesSections.sort(function (a, b) {
            return (
                ('number' === typeof a.tiles[0].mi_sequence ? a.tiles[0].mi_sequence : 0) -
                ('number' === typeof b.tiles[0].mi_sequence ? b.tiles[0].mi_sequence : 0)
            );
        });
    } else if (homePageState.filterState.groupBy === 'Freq') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles) {
            if (undefined !== tile.refresh_frequency_text) {
                if (null === tile.refresh_frequency_text) tile.refresh_frequency_text = 'Unknown';
                let k = '' + tile.refresh_frequency_text;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }
        }

        for (let key in sectionsMap)
            if (undefined !== sectionsMap[key]) tilesSections.push({ title: key, k: key, tiles: sectionsMap[key] });

        tilesSections.sort(function (a, b) {
            if (a.title == 'Unknown') return 1;
            else if (b.title == 'Unknown') return -1;
            else
                return (
                    ('number' === typeof a.tiles[0].refresh_frequency_sec ? a.tiles[0].refresh_frequency_sec : 0) -
                    ('number' === typeof b.tiles[0].refresh_frequency_sec ? b.tiles[0].refresh_frequency_sec : 0)
                );
        });
    } else if (homePageState.filterState.groupBy === 'None') {
        tilesSections.push({ title: '', k: 'group_by_none', tiles: [] });
        let ids: { [key: string]: number } = {};

        for (const tile of filteredTiles) {
            let key = tile.element_id + '_' + tile.segment_value_id;
            if (undefined === ids[key]) {
                ids[key] = 1;
                tilesSections[0].tiles.push(tile);
            }
        }

        tilesSections[0].tiles.sort(categorySort);
    } else if (homePageState.filterState.groupBy === 'LastModified') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};

        for (const tile of filteredTiles)
            if (undefined !== tile.last_modified) {
                let k = tile.last_modified;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }

        let titles = [
            t('label_updated_today'),
            t('label_updated_last_week'),
            t('label_updated_month'),
            t('label_updated_more_than_month'),
            t('label_updated_unknown'),
        ];

        let order = ['today', 'last_week', 'last_month', 'more_than_month', 'unknown'];

        for (let i = 0; i < titles.length; i++) {
            let k = order[i];
            if (undefined !== sectionsMap[k]) tilesSections.push({ title: titles[i], k: k, tiles: sectionsMap[k] });
        }
    } else if (homePageState.filterState.groupBy === 'Certified') {
        let sectionsMap: { [key: string]: TileInfo[] } = {};
        for (const tile of filteredTiles)
            if (undefined !== tile.certified_ind) {
                let k = tile.certified_ind;
                if (undefined === sectionsMap[k]) sectionsMap[k] = [];
                sectionsMap[k].push(tile);
            }

        let titles = { Y: t('label_certified'), N: t('label_uncertified') };

        for (const [k, v] of Object.entries(titles)) {
            if (undefined !== sectionsMap[k]) tilesSections.push({ title: String(v), k: k, tiles: sectionsMap[k] });
        }
    }

    return tilesSections;
};

export const changeTilesOrder = async (
    categoryId: number,
    favoriteId: number,
    folderId: number,
    udeiId: string,
    nextUdeiId: string,
) => {
    return await foldersAPI.changeTilesOrder(categoryId ?? 0, favoriteId ?? 0, folderId ?? 0, udeiId, nextUdeiId);
};
